import type { OperationType, TransactionStatus, TransactionType } from "./card-movements.types";

export const StatementPeriodMode = {
  MONTH: "month",
  RANGE: "range",
  LIFETIME: "lifetime",
} as const;

export type StatementPeriodMode = (typeof StatementPeriodMode)[keyof typeof StatementPeriodMode];

/** Query params for `GET /internal/cards/:id/statement`. */
export interface CardStatementParams {
  mode: StatementPeriodMode;
  /** `YYYY-MM`. Required when mode=month. */
  month?: string;
  /** `YYYY-MM-DD`. Required when mode=range. */
  from?: string;
  /** `YYYY-MM-DD`. Required when mode=range. */
  to?: string;
}

export interface CardStatementAddress {
  address_line_1: string;
  address_line_2: string | null;
  city: string;
  state: string;
  country: string;
  postal_code: string | null;
}

export interface CardStatementPeriod {
  mode: StatementPeriodMode;
  from: string;
  to: string;
  timezone: string;
  /** Rows arrive chronologically. Do not re-sort. */
  order: "ASC";
}

export interface CardStatementCard {
  id: string;
  alias: string | null;
  last_4: string;
  card_type: string;
  card_provider: string;
  expiration_date: string | null;
  status: string;
  operational_country: string;
  created_at: string;
}

export interface CardStatementHolder {
  display_name: string;
  identity_type: string | null;
  document_type: string | null;
  document_number: string | null;
  email: string | null;
  address: CardStatementAddress | null;
  /** `CARD_FALLBACK` means the identity was unavailable, so document and address are missing. */
  source: "IDENTITY" | "CARD_FALLBACK";
}

export interface CardStatementAccount {
  organization_id: string;
  name: string;
  /** Only present when the organization has a corporate business identity. */
  tax_id: string | null;
  address: CardStatementAddress | null;
}

export interface CardStatementFlow {
  amount: number;
  count: number;
}

export interface CardStatementTotals {
  loaded: CardStatementFlow;
  spent: CardStatementFlow;
  net: number;
  opening_balance: number;
  closing_balance: number;
  available_balance: number;
  declined_count: number;
  pending_count: number;
  currency: string;
}

/**
 * Opening and closing balances are derived from the movements, not stored. When `reconciled` is false the
 * statement is missing movements that changed the balance, so both must be shown as estimates.
 */
export interface CardStatementReconciliation {
  reconciled: boolean;
  derived_lifetime_opening: number;
  missing_usd_amount_count: number;
  tolerance: number;
}

export interface CardStatementTransactionLocation {
  country: string | null;
  city: string | null;
}

export interface CardStatementTransaction {
  id: string;
  /** Last 8 characters of the id, uppercased. */
  reference: string;
  created_at: string;
  merchant_name: string | null;
  description: string | null;
  merchant_category_code: string | null;
  transaction_type: TransactionType;
  operation_type: OperationType | null;
  status: TransactionStatus;
  amount: number;
  currency: string;
  usd_amount: number | null;
  exchange_rate: number | null;
  location: CardStatementTransactionLocation | null;
}

export interface CardStatementResponse {
  statement_number: string;
  issued_at: string;
  period: CardStatementPeriod;
  card: CardStatementCard;
  holder: CardStatementHolder;
  account: CardStatementAccount;
  totals: CardStatementTotals;
  reconciliation: CardStatementReconciliation;
  transactions: CardStatementTransaction[];
  transaction_count: number;
  returned_count: number;
  truncated: boolean;
}
